import React, { useState, useEffect, useCallback } from 'react';
import { CloseIcon, WhatsAppIcon, MapIcon, LinkIcon, EditIcon, BookmarkIcon, BookmarkBorderIcon, PhotoIcon, PhoneIcon } from '../Icons';
import { getImageURL } from '../../utils/imageUtils';
import { authService } from '../../services/api';
import { commentsService, consumerAuthService } from '../../services/appApi';

const DetailsPanel = ({
  selectedLand,
  onClose,
  formatPrice,
  formatCoordinates,
  openImageGallery,
  openWhatsAppDialog,
  shareOnWhatsApp,
  openGoogleMaps,
  openKuwaitFinder,
  openAdminEdit,
  downloadSinglePicture,
  onOpenFullDetails,
  onToggleSave,
  isSaved,
  isAuthenticated,
  onTagClick,
  agents = [],
  t,
  language
}) => {
  const isAdmin = authService.isAuthenticated();
  const [callDialogOpen, setCallDialogOpen] = useState(false);

  if (!selectedLand) return null;

  const availableAgents = agents.filter((agent) => agent.agent === true);

  return (
    <div dir="rtl" className="flex flex-col h-full bg-white/90 backdrop-blur-md md:bg-white md:backdrop-blur-none overflow-hidden">
      {/* Drag handle — mobile only, signals this is a swipe-away sheet
          even though dismissal is only wired to backdrop tap / the X
          button below (no drag gesture implemented). */}
      <div className="md:hidden flex justify-center pt-2 pb-1 flex-shrink-0">
        <div className="w-10 h-1.5 rounded-full bg-gray-300" />
      </div>

      {/* Header */}
      <div className="p-2 md:p-4 bg-gray-800 text-white flex justify-between items-center flex-shrink-0">
        <h2 className="text-sm md:text-lg font-bold">
          {t('تفاصيل العقار', 'Property Details')}
        </h2>
        <button
          onClick={onClose}
          className="p-1 hover:bg-white/10 rounded-lg transition-colors"
        >
          <CloseIcon className="w-4 h-4 md:w-5 md:h-5" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Property Image — a bottom gradient scrim keeps the price/gallery
            overlay legible against any photo, instead of flat pill badges. */}
        {selectedLand.picture_url ? (
          <div className="relative overflow-hidden">
            <img
              src={getImageURL(selectedLand.picture_url)}
              alt={`Property ${selectedLand.code_number}`}
              className="w-full h-36 md:h-56 object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-20 md:h-28 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 p-2.5 md:p-4 flex items-end justify-between gap-2">
              <div className="flex flex-col items-start gap-1">
                {selectedLand.land_type_info && (
                  <span
                    className="px-2 py-0.5 md:px-3 md:py-1 text-white text-[10px] md:text-xs font-bold rounded-full shadow-md"
                    style={{
                      backgroundColor: selectedLand.land_type_info.name === 'land' ? '#8B4513' :
                                     selectedLand.land_type_info.name === 'house' ? '#228B22' :
                                     selectedLand.land_type_info.name === 'building' ? '#4682B4' :
                                     selectedLand.land_type_info.name === 'apartment' ? '#32CD32' :
                                     selectedLand.land_type_info.name === 'villa' ? '#FF6347' :
                                     selectedLand.land_type_info.name === 'commercial' ? '#FF8C00' : '#8B4513'
                    }}
                  >
                    {language === 'ar' ? selectedLand.land_type_info.display_name_ar : selectedLand.land_type_info.display_name_en}
                  </span>
                )}
                {selectedLand.price && (
                  <span className="text-white font-bold text-base md:text-2xl drop-shadow-md">
                    {formatPrice(selectedLand.price)}
                  </span>
                )}
              </div>
              <button
                onClick={() => openImageGallery(selectedLand)}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-white/90 hover:bg-white text-gray-800 text-[10px] md:text-xs font-semibold rounded-lg shadow-md backdrop-blur-sm transition-colors flex-shrink-0"
              >
                <PhotoIcon className="w-3.5 h-3.5" />
                {t('الصور', 'Photos')}
              </button>
            </div>
          </div>
        ) : (
          selectedLand.latitude && selectedLand.longitude && (
            <div className="h-36 md:h-56 bg-gray-200">
              <iframe
                src={`https://gis.paci.gov.kw/thumbnail.html?key=PACI_bi5D4e7TrfV8rj&X=${selectedLand.longitude}&Y=${selectedLand.latitude}`}
                width="100%"
                height="100%"
                title="Street View"
                className="border-0"
              />
            </div>
          )
        )}

        {/* Property Information */}
        <div className="p-2 md:p-4 space-y-2 md:space-y-4">
          {/* Title */}
          <h3 className="text-center text-sm md:text-lg font-bold text-gray-800">
            {[
              selectedLand.code_number && `${t('كود:', 'Code:')} ${selectedLand.code_number}`,
              selectedLand.land_number && `${t('القسيمة:', 'Plot:')} ${selectedLand.land_number}`
            ].filter(Boolean).join(' | ') || t('تفاصيل العقار', 'Property Details')}
          </h3>

          {/* Tags */}
          {selectedLand.tags && selectedLand.tags.length > 0 && (
            <div className="flex gap-1 md:gap-2 flex-wrap justify-center">
              {selectedLand.tags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => onTagClick && onTagClick(tag)}
                  className="px-1.5 py-0.5 md:px-2 md:py-1 text-white text-[10px] md:text-xs font-bold rounded hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: tag.color }}
                >
                  {tag.title}
                </button>
              ))}
            </div>
          )}

          {/* Staff-only: has this PACI plot been photographed on-site yet
              (see the shooting table)? Never sent to the API response at
              all for a non-staff request, so isAdmin here is just UI
              polish on top of a server-side guarantee. */}
          {isAdmin && selectedLand.is_shooted && (
            <div className="flex justify-center">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#977d3f] text-white text-[10px] md:text-xs font-bold rounded-full">
                <PhotoIcon className="w-3 h-3" />
                {t('تم تصويرها', 'Photographed')}
              </span>
            </div>
          )}

          {/* Property Details */}
          <div className="space-y-1.5 md:space-y-3">
            {/* Description */}
            {selectedLand.notes && (
              <div className="p-2 md:p-3 bg-white/80 rounded-lg">
                <p className="text-[10px] md:text-xs text-gray-600 mb-0.5 md:mb-1">
                  {t('الوصف', 'Description')}
                </p>
                <p className="text-xs md:text-sm leading-relaxed whitespace-pre-line">
                  {selectedLand.notes}
                </p>
              </div>
            )}

            {/* Additional Pictures */}
            {selectedLand.additional_pictures && selectedLand.additional_pictures.length > 0 && (
              <div className="p-2 md:p-3 bg-white/80 rounded-lg">
                <p className="text-[10px] md:text-xs text-gray-600 mb-1 md:mb-2">
                  {t('صور إضافية', 'Additional Pictures')} ({selectedLand.additional_pictures.length})
                </p>
                <div className="grid grid-cols-2 gap-1.5 md:gap-2">
                  {selectedLand.additional_pictures.slice(0, 4).map((pictureUrl, index) => (
                    <div
                      key={index}
                      className="relative pt-[100%] cursor-pointer rounded-lg overflow-hidden hover:scale-105 transition-transform"
                      onClick={() => downloadSinglePicture(pictureUrl, selectedLand)}
                    >
                      <img
                        src={getImageURL(pictureUrl)}
                        alt={`Additional ${index + 1}`}
                        className="absolute top-0 left-0 w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                {selectedLand.additional_pictures.length > 4 && (
                  <p className="text-[10px] md:text-xs text-gray-600 mt-2 text-center">
                    +{selectedLand.additional_pictures.length - 4} {t('المزيد', 'more')}
                  </p>
                )}
              </div>
            )}

            {/* Comments */}
            <CommentsSection landId={selectedLand.id} t={t} />
          </div>
        </div>
      </div>
      
      {/* Actions Footer — icon-only on mobile (this panel takes the full
          screen width there), icon+label from md: up where there's room. */}
      {/* pb-[...] replaces the bottom side of p-2/md:p-4 with a value that
          also accounts for a mobile browser's own gesture bar/home
          indicator, which otherwise overlaps these buttons — same fix as
          the mobile app's DetailsPanel footer. */}
      <div className="pt-2 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:pt-4 md:px-4 md:pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-gray-200 bg-white/95 flex-shrink-0">
        <div className="flex md:grid md:grid-cols-2 gap-1.5 md:gap-2">
          <button
            onClick={() => shareOnWhatsApp(selectedLand)}
            title={t('تواصل عبر الواتساب', 'Contact via WhatsApp')}
            className="flex-1 md:flex-none flex items-center justify-center gap-0 md:gap-2 px-2 py-2 md:px-4 md:py-3 bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all"
          >
            <WhatsAppIcon className="w-5 h-5" />
            <span className="hidden md:inline">{t('إرسال واتساب', 'Send WhatsApp')}</span>
          </button>
          <button
            onClick={openWhatsAppDialog}
            title={t('تواصل مع وكيل عبر واتساب', 'Contact an Agent via WhatsApp')}
            className="flex-1 md:flex-none flex items-center justify-center gap-0 md:gap-2 px-2 py-2 md:px-3 border-2 border-green-600 text-green-600 hover:bg-green-50 text-sm font-medium rounded-lg transition-colors"
          >
            <WhatsAppIcon className="w-4 h-4" />
            <span className="hidden md:inline">{t('تواصل مع وكيل', 'Contact Agent')}</span>
          </button>
          <button
            onClick={() => setCallDialogOpen(true)}
            title={t('اتصل بوكيل', 'Call an Agent')}
            className="flex-1 md:flex-none flex items-center justify-center gap-0 md:gap-2 px-2 py-2 md:px-3 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-sm font-medium rounded-lg transition-colors"
          >
            <PhoneIcon className="w-4 h-4" />
            <span className="hidden md:inline">{t('اتصال بوكيل', 'Call Agent')}</span>
          </button>
          <button
            onClick={() => openGoogleMaps(selectedLand.latitude, selectedLand.longitude)}
            title={t('خرائط', 'Maps')}
            className="flex-1 md:flex-none flex items-center justify-center gap-0 md:gap-2 px-2 py-2 md:px-3 border-2 border-gray-700 text-gray-700 hover:bg-gray-50 text-sm font-medium rounded-lg transition-colors"
          >
            <MapIcon className="w-4 h-4" />
            <span className="hidden md:inline">{t('خرائط', 'Maps')}</span>
          </button>
          {selectedLand.kuwait_finder_link && (
            <button
              onClick={() => openKuwaitFinder(selectedLand.kuwait_finder_link)}
              title={t('كويت فايندر', 'Finder')}
              className="flex-1 md:flex-none flex items-center justify-center gap-0 md:gap-2 px-2 py-2 md:px-3 border-2 border-yellow-700 text-yellow-700 hover:bg-yellow-50 text-sm font-medium rounded-lg transition-colors"
            >
              <LinkIcon className="w-4 h-4" />
              <span className="hidden md:inline">{t('كويت فايندر', 'Finder')}</span>
            </button>
          )}
          {/* Save Button */}
          {onToggleSave && (
            <button
              onClick={() => onToggleSave(selectedLand.id)}
              className={`flex-1 md:flex-none flex items-center justify-center gap-0 md:gap-2 px-2 py-2 md:px-3 border-2 text-sm font-medium rounded-lg transition-all ${
                isSaved
                  ? 'border-primary bg-primary text-white hover:bg-primary-dark'
                  : 'border-primary text-primary hover:bg-primary/10'
              }`}
              title={isSaved ? t('إزالة من المحفوظات', 'Remove from saved') : t('حفظ', 'Save')}
            >
              {isSaved ? <BookmarkIcon className="w-4 h-4" filled={true} /> : <BookmarkBorderIcon className="w-4 h-4" />}
              <span className="hidden md:inline">{isSaved ? t('محفوظ', 'Saved') : t('حفظ', 'Save')}</span>
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => openAdminEdit(selectedLand.code_number)}
              title={t('تعديل', 'Edit')}
              className="flex-1 md:flex-none md:col-span-2 flex items-center justify-center gap-0 md:gap-2 px-2 py-2 md:px-3 border-2 border-gray-800 text-gray-800 hover:bg-gray-50 text-sm font-medium rounded-lg transition-colors"
            >
              <EditIcon className="w-4 h-4" />
              <span className="hidden md:inline">{t('تعديل', 'Edit')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Call an Agent popup — mirrors WhatsAppHandler's agent dialog, but
          dials the agent's number directly instead of opening a chat. */}
      {callDialogOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden min-w-[320px] max-w-md">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold">
                {t('اتصل بأحد الوكلاء', 'Call an Agent')}
              </h2>
              <button
                onClick={() => setCallDialogOpen(false)}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 bg-gradient-to-br from-yellow-50 to-gray-50 max-h-[60vh] overflow-y-auto">
              {availableAgents.length === 0 ? (
                <p className="text-center py-8 text-gray-600">
                  {t('لا يوجد وكلاء متاحون حالياً', 'No agents available at the moment')}
                </p>
              ) : (
                <div className="space-y-3">
                  {availableAgents.map((agent) => (
                    <a
                      key={agent.id}
                      href={`tel:${agent.phone || agent.phone_number}`}
                      onClick={() => setCallDialogOpen(false)}
                      className="w-full flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-blue-500 hover:shadow-lg transition-all duration-200 group"
                    >
                      <div className="flex-1 text-right">
                        <h3 className="font-bold text-gray-900 text-base">
                          {agent.name}
                        </h3>
                        <p className="text-sm text-gray-600" dir="ltr">
                          {agent.phone || agent.phone_number}
                        </p>
                      </div>
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <PhoneIcon className="w-6 h-6 text-white" />
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CommentsSection = ({ landId, t }) => {
  const isSignedIn = consumerAuthService.isSignedIn();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await commentsService.getForLand(landId);
      setComments(data);
    } catch (err) {
      // Silent — comments are a nice-to-have, not worth an error banner here.
    } finally {
      setLoading(false);
    }
  }, [landId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async () => {
    if (!text.trim()) return;
    try {
      setSubmitting(true);
      await commentsService.create(landId, text.trim());
      setText('');
      await fetchComments();
    } catch (err) {
      alert(t('تعذر إرسال التعليق', 'Could not post comment'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-3 bg-white/80 rounded-lg">
      <p className="text-xs text-gray-600 mb-2">
        {t('التعليقات', 'Comments')} {comments.length > 0 ? `(${comments.length})` : ''}
      </p>
      {loading ? (
        <p className="text-xs text-gray-500">{t('جاري التحميل...', 'Loading...')}</p>
      ) : comments.length === 0 ? (
        <p className="text-xs text-gray-500">{t('لا توجد تعليقات بعد', 'No comments yet')}</p>
      ) : (
        <div className="space-y-2 mb-2">
          {comments.map((c) => (
            <div key={c.id} className="border-b border-gray-100 pb-2 last:border-0 last:pb-0">
              <p className="text-xs font-bold text-gray-700">{c.user_name || t('مستخدم', 'User')}</p>
              <p className="text-sm text-gray-800 whitespace-pre-line">{c.text}</p>
            </div>
          ))}
        </div>
      )}
      {isSignedIn ? (
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('أضف تعليقًا...', 'Add a comment...')}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1.5 bg-primary text-white text-sm rounded-lg disabled:opacity-50"
          >
            {t('إرسال', 'Send')}
          </button>
        </div>
      ) : (
        <p className="text-xs text-gray-500 mt-2">{t('سجل الدخول لإضافة تعليق', 'Sign in to add a comment')}</p>
      )}
    </div>
  );
};

export default DetailsPanel;
